import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {translate, MarkdownViewer} from 'views/module';

import {UPDATE_TYPES} from './updates/index';

import displayResource from 'utils/miscellaneous/display_resource';
import moment from 'moment';

const LogFeedBox = (props) => {
  const [mouseOver, setMouseOver] = React.useState(false);

  const {
    log,
  } = props;

  const logMessage = UPDATE_TYPES[log.updateType];
  if (!logMessage) {
    // don't return anything if a log message doesn't exist for that update type
    return null;
  };

  const submittedBy = log.requester;
  const author = displayResource(submittedBy);
  const logMessageBody = log?.message?.blob;
  const createdAtMoment = moment(log.createdAt);
  const isToday = createdAtMoment.isSame(moment(), 'day');

  const logTitle = React.useMemo(() => {
    switch (log.updateType) {
      case 'CLIENT_CREATE':
        return translate('client.ClientDetail.Correspondance.Feed.Titles.ClientCreated');
      case 'CLIENT_EDIT_NAME':
        return translate('client.ClientDetail.Correspondance.Feed.Titles.NameUpdated');
      case 'CLIENT_EDIT_STATUS':
        return translate('client.ClientDetail.Correspondance.Feed.Titles.StatusUpdated');
      case 'CLIENT_EDIT_CONTACT':
        return translate('client.ClientDetail.Correspondance.Feed.Titles.ContactAdded');
      case 'CLIENT_REMOVE_CONTACT':
        return translate('client.ClientDetail.Correspondance.Feed.Titles.ContactRemoved');
      default:
        return translate(logMessage.title);
    }
  }, [log.updateType, logMessage.title]);

  const timelineLabel = React.useMemo(() => {
    if (!isToday) {
      return {
        primary: createdAtMoment.format('MMM D'),
        secondary: createdAtMoment.format('h:mm A'),
      };
    }

    const diffMinutes = Math.max(moment().diff(createdAtMoment, 'minutes'), 0);
    if (diffMinutes < 1) {
      return {
        primary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.Today'),
        secondary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.Now'),
      };
    }

    if (diffMinutes < 60) {
      return {
        primary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.Today'),
        secondary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.MinutesAgo', {
          count: diffMinutes,
        }),
      };
    }

    const diffHours = Math.max(moment().diff(createdAtMoment, 'hours'), 1);
    return {
      primary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.Today'),
      secondary: translate('client.ClientDetail.Correspondance.Feed.RelativeTime.HoursAgo', {
        count: diffHours,
      }),
    };
  }, [createdAtMoment, isToday]);

  const Component = logMessage.component;

  return (
    <Box
      onMouseOver={(_) => setMouseOver(true)}
      onMouseLeave={(_) => setMouseOver(false)}
      sx={{
        borderRadius: '10px',
        backgroundColor: mouseOver ? '#F9F8F6' : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
    >
      <Stack
        direction='row'
        spacing={{md: 2, xs: 1.25}}
        alignItems='flex-start'
        sx={{
          py: 0.5,
          px: 0.5,
        }}
      >
        <Stack
          spacing={0.1}
          sx={{
            width: '64px',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.primary}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.secondary}
          </Typography>
        </Stack>
        <Box
          sx={{
            pt: '4px',
            width: '10px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#D8D3CC',
              border: '1.5px solid #D8D3CC',
            }}
          />
        </Box>
        <Stack
          spacing={0.75}
          sx={{
            minWidth: 0,
            flex: 1,
            pr: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              lineHeight: '17px',
              fontWeight: 500,
              color: '#1A1916',
            }}
          >
            {logTitle}
          </Typography>
          {
            author &&
            <Stack direction='row' spacing={0.75}>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '19px',
                  color: '#6B6760',
                }}
              >
                {translate('client.ClientDetail.Correspondance.Feed.Metadata.Logs')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '19px',
                  color: '#6B6760',
                }}
              >
                {`${'\u00B7'} ${author}`}
              </Typography>
            </Stack>
          }
          <Box
            sx={{
              fontSize: '12px',
              lineHeight: '19px',
              color: '#1A1916',
            }}
          >
            <Component
              requesterName={author}
              oldData={log.oldData}
              newData={log.newData}
              timestampStyle={{
                fontSize: '12px',
                lineHeight: '19px',
                color: '#6B6760',
              }}
            />
          </Box>
          {
            logMessageBody && (
              <Box>
                <MarkdownViewer value={logMessageBody} truncate />
              </Box>
            )
          }
        </Stack>
      </Stack>
    </Box>
  );
};
LogFeedBox.propTypes = {
  log: PropTypes.object.isRequired,
};

export default React.memo(LogFeedBox);
